import { useCallback, useEffect, useState } from "react";
import { FlaskConical, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const OUT = [
  ["both_allowed", "Consentito da entrambi", "#10B981"],
  ["both_blocked", "Bloccato da entrambi", "#64748B"],
  ["legacy_allowed_pc_blocked", "Legacy consente · per canale blocca", "#F59E0B"],
  ["legacy_blocked_pc_allowed", "Legacy blocca · per canale consente", "#3B82F6"],
  ["error", "Errore di valutazione ombra", "#EF4444"],
];
const PURPOSE = { marketing: "Marketing", service: "Servizio", assistance: "Assistenza", transactional: "Transazionale", otp: "OTP" };

const Chips = ({ value, onChange, items, testid }) => (
  <div className="flex flex-wrap gap-1" data-testid={testid}>
    {items.map(([k, label]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k || "all"}`}
              className={`rounded-full px-2.5 h-7 text-[11px] transition-colors ${value === k ? "bg-slate-200 text-slate-900" : "bg-[#1E293B] border border-[#1F2937] text-slate-300 hover:text-slate-100"}`}>{label}</button>
    ))}
  </div>
);

export const ConsentShadowPanel = () => {
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ days: 30, channel: "", purpose: "" });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: f.days };
      if (f.channel) params.channel = f.channel;
      if (f.purpose) params.purpose = f.purpose;
      const r = await api.get("/consents/shadow/summary", { params });
      setD(r.data); setDenied(false);
    } catch (e) { if (e.response?.status === 403) setDenied(true); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  if (denied || (!d && !loading)) return null;
  const tot = d?.totals || {};
  return (
    <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4" data-testid="consent-shadow-panel">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-base md:text-lg font-semibold text-slate-100 flex items-center gap-2"><FlaskConical className="h-5 w-5 text-[#F59E0B]" /> Motore consensi — modalità ombra</h2>
          <p className="text-xs text-slate-400 mt-1">Statistiche di <b>simulazione</b>: confronto tra la decisione legacy applicata e il motore per canale. Nessun invio, nessun dato personale.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B]" data-testid="consent-shadow-sim-badge">SIMULAZIONE · nessun invio</span>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${d?.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`} data-testid="consent-shadow-enabled">
            Ombra {d?.enabled ? "ATTIVA" : "disattivata"} · modello operativo {d?.operative_model || "legacy"}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {[7, 30, 90].map((n) => <button key={n} onClick={() => setF({ ...f, days: n })} data-testid={`consent-shadow-days-${n}`} className={`rounded-full px-3 h-8 text-xs ${f.days === n ? "bg-[#10B981] text-white" : "bg-[#1E293B] border border-[#1F2937] text-slate-300"}`}>{n} giorni</button>)}
        <Chips value={f.channel} onChange={(v) => setF({ ...f, channel: v })} testid="consent-shadow-channel" items={[["", "Tutti i canali"], ["whatsapp", "WhatsApp"], ["sms", "SMS"]]} />
        <Chips value={f.purpose} onChange={(v) => setF({ ...f, purpose: v })} testid="consent-shadow-purpose" items={[["", "Tutte le finalità"], ...Object.entries(PURPOSE)]} />
        <button onClick={load} className="ml-auto text-slate-400 hover:text-slate-200" data-testid="consent-shadow-refresh" aria-label="Aggiorna">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {OUT.map(([k, label, color]) => (
          <div key={k} className="rounded-xl border border-[#1F2937] bg-[#1E293B] p-3" data-testid={`consent-shadow-total-${k}`}>
            <p className="text-2xl font-bold" style={{ color }}>{tot[k] ?? 0}</p>
            <p className="text-[11px] text-slate-400 leading-tight">{label}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400" data-testid="consent-shadow-evaluations">
        {d?.evaluations ?? 0} valutazioni simulate dal {d?.since} · {d?.divergences ?? 0} divergenze · conservazione {d?.retention_days} giorni
      </p>
      {(d?.by_context || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="consent-shadow-contexts">
            <thead><tr className="text-slate-400 text-left"><th className="py-1 pr-2">Percorso</th><th className="py-1 pr-2 text-right whitespace-nowrap">Valutazioni</th><th className="py-1 pr-2 text-right whitespace-nowrap">Dest. unici</th><th className="py-1 text-right whitespace-nowrap">Diverg.</th></tr></thead>
            <tbody>{d.by_context.map((c) => (
              <tr key={c.context} className="border-t border-[#1F2937] text-slate-200" data-testid={`consent-shadow-ctx-${c.context}`}>
                <td className="py-1.5 pr-2"><span className="text-slate-500 mr-1">{c.context}</span>{c.label}</td>
                <td className="py-1.5 pr-2 text-right">{c.total}</td>
                <td className="py-1.5 pr-2 text-right">{c.unique === null ? <span className="text-slate-500">n/d</span> : c.unique}</td>
                <td className="py-1.5 text-right text-[#F59E0B]">{(c.legacy_allowed_pc_blocked || 0) + (c.legacy_blocked_pc_allowed || 0)}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {(d?.by_reason || []).length > 0 && (
        <div className="overflow-x-auto">
          <p className="text-xs font-semibold text-slate-300 mb-1">Divergenze per motivo</p>
          <table className="w-full text-xs" data-testid="consent-shadow-reasons">
            <thead><tr className="text-slate-400 text-left"><th className="py-1 pr-2">Esito</th><th className="py-1 pr-2">Canale</th><th className="py-1 pr-2">Finalità</th><th className="py-1 pr-2">Legacy → Motore</th><th className="py-1 text-right">N.</th></tr></thead>
            <tbody>{d.by_reason.map((r, i) => (
              <tr key={i} className="border-t border-[#1F2937] text-slate-200">
                <td className="py-1.5 pr-2">{OUT.find((o) => o[0] === r.outcome)?.[1] || r.outcome}</td><td className="py-1.5 pr-2">{r.channel === "sms" ? "SMS" : "WhatsApp"}</td>
                <td className="py-1.5 pr-2">{PURPOSE[r.purpose] || r.purpose}</td><td className="py-1.5 pr-2 font-mono">{r.legacy_code} → {r.engine_code}</td><td className="py-1.5 text-right">{r.count}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {d && d.evaluations === 0 && <p className="text-xs text-slate-500" data-testid="consent-shadow-empty">Nessuna valutazione nel periodo{d.enabled ? "" : " (modalità ombra disattivata: attivabile solo dal Super Admin)"}.</p>}
    </div>
  );
};
